/**
 * @description
 * Custom hooks for user management functionality.
 * Provides state management and API integration for client users and API keys.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';

// Types
interface ClientUser {
  id: string;
  clientId: string;
  userEmail: string;
  userName: string;
  userId?: string;
  role: 'developer' | 'manager' | 'owner';
  status: 'active' | 'pending' | 'removed';
  apiKeysCount: number;
  anthropicWorkspaceId?: string;
  anthropicApiKeyPreview?: string;
  anthropicApiKeyAssignedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserRequest {
  userEmail: string;
  userName: string;
  role: 'developer' | 'manager' | 'owner';
  syncWithPlatform?: boolean;
}

interface UpdateUserRequest {
  role?: 'developer' | 'manager' | 'owner';
  status?: 'active' | 'pending' | 'removed';
}

interface ApiKey {
  id: string;
  clientUserId: string;
  provider: 'google_ai' | 'openai' | 'anthropic' | 'groq' | 'openrouter' | 'cerebras';
  keyName: string;
  keyPreview: string;
  isActive: boolean;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface CreateApiKeyRequest {
  provider: string;
  keyName: string;
  keyValue: string;
  isActive?: boolean;
}

interface PlatformSyncResult {
  synced: number;
  added: number;
  updated: number;
  removed: number;
  errors: string[];
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UserFilters {
  role?: string;
  status?: string;
  search?: string;
}

/**
 * Hook for managing client users with null safety
 */
export function useClientUsers(clientId: string) {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<UserFilters>({});

  const fetchUsers = useCallback(async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.getClientUsers(clientId, {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      
      // API service now transforms backend response to { users: [], pagination: {} }
      setUsers(response.users || []);
      setPagination(response.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, pagination.page, pagination.limit, filters]);

  const addUser = useCallback(async (userData: CreateUserRequest): Promise<ClientUser | null> => {
    try {
      const response = await api.addClientUser(clientId, userData);
      
      // Add new user to the list
      setUsers(prev => [response.user, ...prev]);
      
      // Show platform sync result if available
      if (response.platformSync) {
        if (response.platformSync.synced) {
          toast.success(`User added and synced with platform`);
        } else {
          toast.warning(`User added locally, but platform sync failed: ${response.platformSync.error}`);
        }
      } else {
        toast.success('User added successfully');
      }
      
      return response.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add user';
      toast.error(errorMessage);
      throw err;
    }
  }, [clientId]);

  const updateUser = useCallback(async (userId: string, updates: UpdateUserRequest): Promise<ClientUser | null> => {
    try {
      const response = await api.updateClientUser(clientId, userId, updates);
      
      // Update user in the list
      setUsers(prev => prev.map(user => 
        user.id === userId ? response.user : user
      ));
      
      toast.success('User updated successfully');
      return response.user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      toast.error(errorMessage);
      throw err;
    }
  }, [clientId]);

  const deleteUser = useCallback(async (userId: string): Promise<void> => {
    try {
      const response = await api.deleteClientUser(clientId, userId);
      
      // Remove user from the list
      setUsers(prev => prev.filter(user => user.id !== userId));
      
      toast.success(`User removed successfully (${response.deletedApiKeys} API keys deleted)`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      toast.error(errorMessage);
      throw err;
    }
  }, [clientId]);

  const searchUsers = useCallback((query: string) => {
    setFilters(prev => ({ ...prev, search: query }));
  }, []);

  const filterUsers = useCallback((newFilters: UserFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const setPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  // Fetch users when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    pagination,
    filters,
    refetch: fetchUsers,
    addUser,
    updateUser,
    deleteUser,
    searchUsers,
    filterUsers,
    setPage,
    setLimit,
  };
}

/**
 * Hook for managing user API keys
 */
export function useUserApiKeys(clientId: string, userId: string) {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const fetchApiKeys = useCallback(async () => {
    if (!clientId || !userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.getUserApiKeys(clientId, userId);
      setApiKeys(response.apiKeys);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch API keys';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, userId]);

  const addApiKey = useCallback(async (keyData: CreateApiKeyRequest): Promise<ApiKey | null> => {
    try {
      const response = await api.addUserApiKey(clientId, userId, keyData);
      
      // Add new API key to the list
      setApiKeys(prev => [response.apiKey, ...prev]);
      
      toast.success('API key added successfully');
      return response.apiKey;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add API key';
      toast.error(errorMessage);
      throw err;
    }
  }, [clientId, userId]);

  const deleteApiKey = useCallback(async (keyId: string): Promise<void> => {
    try {
      await api.deleteUserApiKey(clientId, userId, keyId);
      
      // Remove API key from the list
      setApiKeys(prev => prev.filter(key => key.id !== keyId));
      
      toast.success('API key deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete API key';
      toast.error(errorMessage);
      throw err;
    }
  }, [clientId, userId]);

  const toggleKeyVisibility = useCallback((keyId: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [keyId]: !prev[keyId],
    }));
  }, []);

  // Fetch API keys when dependencies change
  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  return {
    apiKeys,
    loading,
    error,
    showSecrets,
    refetch: fetchApiKeys,
    addApiKey,
    deleteApiKey,
    toggleKeyVisibility,
  };
}

/**
 * Hook for platform synchronization
 */
export function usePlatformSync() {
  const [platformUsers, setPlatformUsers] = useState<ClientUser[]>([]);
  const [loading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'loading' | 'syncing' | 'completed' | 'error'>('idle');
  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    authenticated: boolean;
    userInfo?: { email: string; role: string };
  }>({
    connected: false,
    authenticated: false,
  });

  const verifyConnection = useCallback(async (): Promise<boolean> => {
    try {
      const status = await api.verifyPlatformConnection();
      setConnectionStatus({
        connected: status.connection.connected,
        authenticated: status.connection.authenticated,
        userInfo: status.connection.userInfo,
      });
      return status.connection.connected && status.connection.authenticated;
    } catch (err) {
      console.error('Platform connection verification failed:', err);
      setConnectionStatus({
        connected: false,
        authenticated: false,
      });
      return false;
    }
  }, []);

  const getPlatformUsers = useCallback(async (clientId: string): Promise<ClientUser[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Call real sync endpoint to get platform users
      await api.syncUsersFromPlatform(clientId, {
        forceSync: false,
        syncApiKeys: false
      });
      // syncResult is a PlatformSyncResult object, not an array of users
      // We need to return an empty array since we don't have the actual users list
      setPlatformUsers([]);
      return [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch platform users';
      setError(errorMessage);
      toast.error(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const syncUsers = useCallback(async (
    clientId: string,
    options?: {
      forceSync?: boolean;
      syncApiKeys?: boolean;
    }
  ): Promise<PlatformSyncResult | null> => {
    setSyncStatus('syncing');
    setError(null);
    
    try {
      const response = await api.syncUsersFromPlatform(clientId, options);
      
      setSyncStatus('completed');
      
      // Show sync results
      const { syncResult } = response;
      if (syncResult.errors.length > 0) {
        toast.warning(`Sync completed with ${syncResult.errors.length} errors`);
      } else {
        toast.success(`Sync completed: ${syncResult.added} added, ${syncResult.updated} updated`);
      }
      
      return syncResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Platform sync failed';
      setError(errorMessage);
      setSyncStatus('error');
      toast.error(errorMessage);
      return null;
    }
  }, []);

  return {
    platformUsers,
    loading,
    error,
    syncStatus,
    connectionStatus,
    verifyConnection,
    getPlatformUsers,
    syncUsers,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
